import { useQuery } from "@tanstack/react-query";
import { api } from "../../../shared/api/client";
import { Field, Input, Label, Select } from "../../../shared/ui";
import type {
  EmployeeSummary,
  PageResponse,
  ProjectSummary,
  TeamSummary,
  WorkItemType,
} from "../../../shared/types/api";

/**
 * The filters shared by every delivery report: a window plus optional narrowings.
 *
 * <p>Kept in one place so the four reports look and behave the same, and so a change to what can
 * be filtered on happens once. Empty strings mean "no restriction" and are simply left off the
 * query, which is exactly what the API treats a missing parameter as.
 */
export interface ReportFilterValues {
  from: string;
  to: string;
  department: string;
  projectId: string;
  teamId: string;
  type: string;
  priority: string;
}

const WORK_ITEM_TYPES: WorkItemType[] = [
  "EPIC",
  "USER_STORY",
  "TECHNICAL_TASK",
  "BUG",
  "SPIKE",
  "SUBTASK",
  "ANALYSIS",
  "DEMO_PREPARATION",
  "MIGRATION",
  "SERVER_WORK",
];
const PRIORITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

/** A code like USER_STORY as the words a person reads: "User story". */
export function titleize(code: string): string {
  const lower = code.replace(/_/g, " ").toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function iso(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
}

/** The default window a report opens on: the last ninety days up to today. */
export function defaultFilters(): ReportFilterValues {
  const to = new Date();
  const from = new Date(to.getTime() - 90 * 86_400_000);
  return {
    from: iso(from),
    to: iso(to),
    department: "",
    projectId: "",
    teamId: "",
    type: "",
    priority: "",
  };
}

/** The filters as a query string, dropping everything left blank. */
export function toQuery(values: ReportFilterValues, extra?: Record<string, string>): string {
  const params = new URLSearchParams();
  params.set("from", values.from);
  params.set("to", values.to);
  if (values.department) params.set("department", values.department);
  if (values.projectId) params.set("projectId", values.projectId);
  if (values.teamId) params.set("teamId", values.teamId);
  if (values.type) params.set("type", values.type);
  if (values.priority) params.set("priority", values.priority);
  for (const [key, value] of Object.entries(extra ?? {})) {
    if (value) params.set(key, value);
  }
  return params.toString();
}

/**
 * The filter row above a report. Department, project and team options are loaded once and cached;
 * a report that names an employee (the scorecard) hides the department filter, because the person
 * already fixes it.
 */
export function FilterBar({
  values,
  onChange,
  showDepartment = true,
}: {
  values: ReportFilterValues;
  onChange: (next: ReportFilterValues) => void;
  showDepartment?: boolean;
}) {
  const departments = useQuery({
    queryKey: ["report-filter", "departments"],
    queryFn: () => api.get<string[]>("/api/v1/employees/departments"),
    enabled: showDepartment,
  });
  const projects = useQuery({
    queryKey: ["report-filter", "projects"],
    queryFn: () =>
      api.get<PageResponse<ProjectSummary>>("/api/v1/projects?size=200&sort=name,asc"),
  });
  const teams = useQuery({
    queryKey: ["report-filter", "teams"],
    queryFn: () => api.get<PageResponse<TeamSummary>>("/api/v1/teams?size=200&sort=name,asc"),
  });

  const set =
    (key: keyof ReportFilterValues) =>
    (event: { target: { value: string } }) =>
      onChange({ ...values, [key]: event.target.value });

  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
      <Field>
        <Label htmlFor="report-from">From</Label>
        <Input id="report-from" type="date" value={values.from} onChange={set("from")} />
      </Field>
      <Field>
        <Label htmlFor="report-to">To</Label>
        <Input id="report-to" type="date" value={values.to} onChange={set("to")} />
      </Field>

      {showDepartment && (
        <Field>
          <Label htmlFor="report-department">Department</Label>
          <Select id="report-department" value={values.department} onChange={set("department")}>
            <option value="">All departments</option>
            {(departments.data ?? []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </Field>
      )}

      <Field>
        <Label htmlFor="report-project">Project</Label>
        <Select id="report-project" value={values.projectId} onChange={set("projectId")}>
          <option value="">All projects</option>
          {(projects.data?.content ?? []).map((project) => (
            <option key={project.id} value={project.id}>
              {project.projectKey} · {project.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <Label htmlFor="report-team">Team</Label>
        <Select id="report-team" value={values.teamId} onChange={set("teamId")}>
          <option value="">All teams</option>
          {(teams.data?.content ?? []).map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <Label htmlFor="report-type">Type</Label>
        <Select id="report-type" value={values.type} onChange={set("type")}>
          <option value="">All types</option>
          {WORK_ITEM_TYPES.map((type) => (
            <option key={type} value={type}>
              {titleize(type)}
            </option>
          ))}
        </Select>
      </Field>

      <Field>
        <Label htmlFor="report-priority">Priority</Label>
        <Select id="report-priority" value={values.priority} onChange={set("priority")}>
          <option value="">All priorities</option>
          {PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {titleize(priority)}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

/** The active employees a scorecard can be run for. */
export function useEmployeeOptions() {
  return useQuery({
    queryKey: ["report-filter", "employees"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?size=500&sort=fullName,asc&active=true",
      ),
  });
}
